import { localeForLanguage } from './numberFormatting';

/**
 * Currency utility functions for formatting amounts with currency symbols
 */

/**
 * Get the currency symbol for a given currency code
 * @param currency - The currency code (USD, EUR, PLN, etc.)
 * @returns The currency symbol
 */
export const getCurrencySymbol = (currency: string): string => {
  const symbols: Record<string, string> = {
    USD: '$',
    EUR: '€',
    PLN: 'zł',
  };
  
  return symbols[currency] || currency;
};

/**
 * Format an amount with the appropriate currency symbol
 * @param amount - The numeric amount to format (can be string or null/undefined)
 * @param currency - The currency code (USD, EUR, PLN, etc.)
 * @returns The formatted currency string
 */
export const formatCurrency = (amount: number | string | null | undefined, currency: string = 'PLN'): string => {
  const symbol = getCurrencySymbol(currency);
  const numericAmount = parseFloat(String(amount ?? 0)) || 0;
  const formatted = numericAmount.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  if (currency === 'PLN') {
    return `${formatted} ${symbol}`;
  }
  return `${symbol}${formatted}`;
};

/**
 * formatCurrency, but ALWAYS grouped: identical symbol presentation
 * ("1 234,56 zł" / "€1 234,56"), with useGrouping 'always' so the thousands
 * separator does not appear and disappear with magnitude.
 *
 * Why this exists separately: Polish CLDR sets minimumGroupingDigits=2, so
 * plain pl-PL formatting leaves 4-digit amounts UNGROUPED ("5625,00 zł") while
 * 5-digit ones group ("68 749,59 zł"). On a ledger/statement surface, where a
 * money column is read down as a list, that inconsistency reads as a bug — the
 * reference spreadsheet groups everything. Same CLDR nuance already documented
 * on formatMoneyPl below, which is not reusable here because it renders an ISO
 * code suffix ("1 234,56 PLN") rather than a symbol.
 *
 * formatCurrency is deliberately left untouched: it is the app-wide default and
 * dozens of surfaces depend on its current output. Opt in per surface.
 *
 * @param amount - The numeric amount to format (can be string or null/undefined)
 * @param currency - The currency code (USD, EUR, PLN, etc.)
 * @returns The formatted currency string, always thousands-grouped
 */
export const formatCurrencyGrouped = (
  amount: number | string | null | undefined,
  currency: string = 'PLN',
): string => {
  const symbol = getCurrencySymbol(currency);
  const numericAmount = parseFloat(String(amount ?? 0)) || 0;
  const formatted = numericAmount.toLocaleString('pl-PL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: 'always',
  } as Intl.NumberFormatOptions);

  if (currency === 'PLN') {
    return `${formatted} ${symbol}`;
  }
  return `${symbol}${formatted}`;
};

/**
 * Format a numeric amount with pl-PL grouping (2dp, NBSP thousands
 * separator) and an ISO currency-code suffix (e.g. "1 234,56 PLN") — the
 * single home for transport-surface money text: rate/total inputs, the
 * contract-sentence Alert, and the implied-unit-rate hint in
 * TransportCostsSection.tsx; the allocations table, cost summary, and
 * headline rate on TransportRouteDetail.tsx; and the rate column on the
 * TransportRoutes.tsx list (S36, 2026-07-07 — unified off formatCurrency's
 * "zł" symbol and Intl.NumberFormat's currency-style symbol, both of which
 * mixed with this ISO-code convention on the same pages). Distinct from
 * formatCurrency above, which renders a currency SYMBOL (zł/$/€); the
 * transport surfaces specifically want the raw ISO code, not a symbol, so
 * the two are kept separate rather than merged.
 * @param value - The numeric amount to format
 * @param currencyCode - The ISO currency code to suffix (USD, EUR, PLN, etc.)
 * @returns The formatted "1 234,56 PLN"-style string
 */
export const formatMoneyPl = (value: number, currencyCode: string): string =>
  // useGrouping 'always': Polish CLDR sets minimumGroupingDigits=2, so plain
  // pl-PL formatting leaves 4-digit amounts UNGROUPED ("4500,00") while antd
  // Statistic (explicit groupSeparator prop) always groups ("4 500,00") — the
  // two would visibly disagree on the same transport detail page for the
  // 1000–9999 range (orchestrator decision, S36 follow-up 2026-07-07).
  `${value.toLocaleString('pl-PL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: 'always',
  } as Intl.NumberFormatOptions)} ${currencyCode}`;

/** Format a transport amount with localized separators and an ISO-code suffix. */
export const formatMoneyLocalized = (
  value: number,
  currencyCode: string,
  language?: string,
): string => `${value.toLocaleString(localeForLanguage(language), {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  useGrouping: 'always',
} as Intl.NumberFormatOptions)} ${currencyCode}`;

/** Format general financial UI in the active application language. */
export const formatCurrencyLocalized = (
  amount: number | string | null | undefined,
  currency = 'PLN',
  language?: string,
): string => formatCurrencyWithLocale(amount, currency, localeForLanguage(language));

/**
 * Format currency with proper locale formatting
 * @param amount - The numeric amount to format (can be string or null/undefined)
 * @param currency - The currency code
 * @param locale - The locale string (e.g., 'en-US', 'pl-PL')
 * @returns The formatted currency string
 */
export const formatCurrencyWithLocale = (
  amount: number | string | null | undefined,
  currency: string = 'PLN',
  locale?: string
): string => {
  // Convert to number and handle null/undefined/invalid values
  const numericAmount = parseFloat(String(amount ?? 0)) || 0;

  try {
    // Try to use the Intl.NumberFormat for better formatting
    const formatter = new Intl.NumberFormat(locale || 'en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return formatter.format(numericAmount);
  } catch (error) {
    // Fallback to simple formatting if Intl is not supported or currency is invalid
    return formatCurrency(numericAmount, currency);
  }
};

/**
 * Locale-aware compact currency (e.g. "1,2 mln zł" / "PLN 1.2M") — single
 * home for the CFO-dashboard-style KPI figures: Profitability.tsx's
 * "Bilans operacyjny" hero/satellites, FixedCosts.tsx's hero/satellites, and
 * the Profitability "Przegląd" overview hero/trend/breakdown (all previously
 * carried their own byte-identical copy of this helper). Detail tables keep
 * full precision via formatCurrency/formatCurrencyLocalized.
 * @param amount - The numeric amount to format
 * @param language - The active i18n language (defaults to Polish)
 * @returns The compact-notation currency string
 */
export const formatCompactPLN = (amount: number, language?: string): string => {
  const locale = localeForLanguage(language);
  try {
    return new Intl.NumberFormat(locale, {
      notation: 'compact',
      maximumFractionDigits: 1,
      style: 'currency',
      currency: 'PLN',
      currencyDisplay: locale === 'pl-PL' ? 'symbol' : 'code',
    }).format(amount);
  } catch {
    return formatCurrency(amount, 'PLN');
  }
};

/**
 * Parse a currency string to get the numeric value
 * @param currencyString - The formatted currency string
 * @returns The numeric value
 */
export const parseCurrency = (currencyString: string): number => {
  // Remove all non-numeric characters except decimal point and minus sign
  const cleanedString = currencyString.replace(/[^0-9.-]/g, '');
  return parseFloat(cleanedString) || 0;
};
